import BodyText from '@lyra/core/components/BodyText'
import DropdownButton from '@lyra/core/components/DropdownButton'
import { MoreVertical } from '@lyra/core/components/Icon'
import Table, { createColumnHelper, TableData, TableProps } from '@lyra/core/components/Table'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatUSD from '@lyra/core/utils/formatUSD'
import { InstrumentType } from '@lyra/web/constants/instruments'
import { MarketId } from '@lyra/web/constants/markets'
import { OpenOrder, TriggerPrice } from '@lyra/web/constants/order'
import {
  Position,
  SPOT_CRITICAL_THRESHOLD,
  SPOT_WARNING_THRESHOLD,
} from '@lyra/web/constants/position'
import { SubaccountData } from '@lyra/web/constants/subaccount'
import useOrderbookTimestamp from '@lyra/web/hooks/useOrderbookTimestamp'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import useUserSettings from '@lyra/web/hooks/useUserSettings'
import {
  formatInstrumentSize,
  getInstrumentPagePath,
  getInstrumentTypePagePath,
  parseInstrumentName,
} from '@lyra/web/utils/instruments'
import { sortPositionsOrOpenOrders } from '@lyra/web/utils/positions'
import { formatMarginTypeLabel } from '@lyra/web/utils/subaccounts'
import { useRouter } from 'next/navigation'
import React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

import PositionCell from '../PositionCell'
import PositionActionDropdown from './PositionActionDropdown'
import PositionCloseButton from './PositionCloseButton'

// No PnL for 30s and absolute pnl pct within 1%
const HIDE_PNL_PERIOD_MS = 30_000
const HIDE_PNL_PERCENT_THRESHOLD = 0.01

type PositionsTableData = {
  position: Position
  subaccount: SubaccountData
  currentPrice?: number
  entryPrice: number
  equity: number
  indexPrice?: number
  instrumentName: string
  instrumentType: InstrumentType
  isLong: boolean
  leverage?: number
  liquidationPrice?: number
  liquidationState?: LiquidationState
  margin?: number
  marketId: MarketId
  openStopLoss?: TriggerPrice
  openTakeProfit?: TriggerPrice
  orderId?: string
  realizedPnl?: number
  realizedPnlPct?: number
  size: number
  subaccountId?: number
  unrealizedPnl?: number
  unrealizedPnlPct?: number
}

type Props = {
  subaccount?: SubaccountData | SubaccountData[]
  showActions?: boolean
  hideSubaccountLabels?: boolean
  hidePnl?: boolean
} & Omit<TableProps<PositionsTableData>, 'data' | 'columns'>

type LiquidationState = 'critical' | 'warning' | 'healthy'

export default function PositionsTable({
  subaccount,
  showActions,
  hideSubaccountLabels,
  hidePnl,
  ...tableProps
}: Props) {
  const { prefetch } = useRouter()
  const { userSettings, updateSettings } = useUserSettings()
  const { subaccount: currentSubaccount } = useSubaccount()

  const subaccounts = useMemo(
    () => (subaccount ? (Array.isArray(subaccount) ? subaccount : [subaccount]) : []),
    [subaccount]
  )

  const hideDustPositions = useMemo(
    () =>
      currentSubaccount
        ? userSettings.hideDustPositions[currentSubaccount.subaccount_id] ?? false
        : false,
    [currentSubaccount, userSettings.hideDustPositions]
  )

  const handleHideDustPositions = useCallback(
    (subaccountId: number) => {
      updateSettings('hideDustPositions', {
        ...userSettings.hideDustPositions,
        [subaccountId]: !userSettings.hideDustPositions[subaccountId],
      })
    },
    [updateSettings, userSettings.hideDustPositions]
  )

  const showSubaccountId = subaccounts.length > 1

  const positions: (Position & { subaccount: SubaccountData })[] = useMemo(() => {
    const positions = subaccounts
      .flatMap((sub) =>
        sub.subaccount.positions.map((position) => ({ ...position, subaccount: sub }))
      )
      .filter((position) => position.amount !== '0')
    return sortPositionsOrOpenOrders(positions)
  }, [subaccounts])

  const openOrders: OpenOrder[] = useMemo(() => {
    const openOrders = subaccounts
      .flatMap((sub) => sub.subaccount.open_orders)
      .filter((order) => ['open', 'untriggered'].includes(order.order_status))
    return sortPositionsOrOpenOrders(openOrders) as OpenOrder[]
  }, [subaccounts])

  const hasMargin = useMemo(
    () => positions.some((position) => +position.maintenance_margin !== 0),
    [positions]
  )

  const hasRealizedPnl = useMemo(
    () =>
      positions.some(
        (position) => +position.realized_pnl_excl_fees !== 0 || +position.cumulative_funding !== 0
      ),
    [positions]
  )

  const positionPagePaths = useMemo(() => {
    // collect unique instrument paths, e.g. options/eth, perps/btc
    return Array.from(
      new Set(
        filterNulls(
          positions.map((position) => getInstrumentTypePagePath(position.instrument_name))
        )
      )
    ).sort()
  }, [positions])

  useEffect(() => {
    // prefetch every instrument page
    positionPagePaths.forEach((pagePath) => prefetch(pagePath))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(positionPagePaths), prefetch])

  const { getTimestamp } = useOrderbookTimestamp()

  const { push } = useRouter()

  const rows: TableData<PositionsTableData>[] = useMemo(() => {
    return filterNulls(
      positions.map(({ subaccount, ...position }) => {
        const {
          unrealized_pnl_excl_fees,
          realized_pnl_excl_fees,
          amount,
          average_price_excl_fees,
          liquidation_price,
          index_price,
          instrument_name,
          creation_timestamp,
          amount_step,
        } = position
        const positionAmount = +amount
        const positionAmountStep = +amount_step
        const isLong = positionAmount > 0
        const positionAmountAbs = Math.abs(positionAmount)
        const avgPrice = +average_price_excl_fees
        const currentPrice = +position.mark_price
        const indexPrice = +index_price
        const liquidationPrice = liquidation_price ? +liquidation_price : undefined

        const unrealizedPnl = +unrealized_pnl_excl_fees
        const realizedPnl = +realized_pnl_excl_fees + +position.cumulative_funding
        const realizedPnlPct = realizedPnl / (positionAmountAbs * avgPrice)
        const unrealizedPnlPct = unrealizedPnl / (positionAmountAbs * avgPrice)
        const hidePnl =
          getTimestamp() - creation_timestamp < HIDE_PNL_PERIOD_MS &&
          Math.abs(unrealizedPnlPct) < HIDE_PNL_PERCENT_THRESHOLD &&
          Math.abs(realizedPnlPct) < HIDE_PNL_PERCENT_THRESHOLD

        const spotPctToLiquidation = liquidationPrice
          ? Math.abs(liquidationPrice - indexPrice) / indexPrice
          : 0
        const liquidationState: LiquidationState = liquidationPrice
          ? spotPctToLiquidation <= SPOT_CRITICAL_THRESHOLD
            ? 'critical'
            : spotPctToLiquidation <= SPOT_WARNING_THRESHOLD
            ? 'warning'
            : 'healthy'
          : 'healthy'
        const leverage = position.leverage ? +position.leverage : undefined

        const instrument = parseInstrumentName(instrument_name)

        if (!instrument || instrument.type === InstrumentType.Spot) {
          return null
        }

        const marketId = instrument?.marketId
        const instrumentType = instrument?.type

        const margin =
          instrumentType === InstrumentType.Perps
            ? // account for unrealized pnl of perps
              -+position.maintenance_margin + +position.mark_value
            : -+position.maintenance_margin

        const openTakeProfit = openOrders?.find(
          (order) =>
            order.instrument_name === instrument_name && order.trigger_type === 'takeprofit'
        )

        const openStopLoss = openOrders?.find(
          (order) => order.instrument_name === instrument_name && order.trigger_type === 'stoploss'
        )

        const path = getInstrumentPagePath(instrument_name)

        const row = {
          position,
          subaccount,
          currentPrice,
          entryPrice: avgPrice,
          equity: positionAmountAbs * currentPrice,
          indexPrice,
          instrumentName: instrument_name,
          instrumentType,
          isLong,
          leverage,
          liquidationPrice,
          liquidationState,
          margin,
          marketId,
          openStopLoss: openStopLoss?.trigger_price,
          openTakeProfit: openTakeProfit?.trigger_price,
          realizedPnl: hidePnl ? 0 : realizedPnl,
          realizedPnlPct: hidePnl ? 0 : realizedPnlPct,
          size: positionAmountAbs,
          unrealizedPnl: hidePnl ? 0 : unrealizedPnl,
          unrealizedPnlPct: hidePnl ? 0 : unrealizedPnlPct,
          stackProps: {
            backgroundColor:
              liquidationState === 'critical'
                ? '$redBg'
                : liquidationState === 'warning'
                ? '$amberBg'
                : undefined,
          },
          onPress: showActions ? () => push(path) : undefined,
        }

        if (userSettings.hideDustPositions[subaccount.subaccount.subaccount_id]) {
          if (Math.abs(positionAmount) < positionAmountStep) {
            return null
          }
        }

        return row
      })
    )
  }, [positions, getTimestamp, openOrders, push, showActions, userSettings.hideDustPositions])

  const columnHelper = createColumnHelper<PositionsTableData>()

  const hasTpOrSl = useMemo(
    () => rows.some((row) => !!row.openStopLoss || row.openTakeProfit),
    [rows]
  )

  const columns = useMemo(
    () =>
      filterNulls([
        columnHelper.accessor('instrumentName', {
          id: 'position',
          header: 'Position',
          meta: {
            minWidth: 160,
          },
          cell: (props) => {
            const { isLong, instrumentName, leverage } = props.row.original
            return (
              <PositionCell instrumentName={instrumentName} isLong={isLong} leverage={leverage} />
            )
          },
        }),
        showSubaccountId
          ? columnHelper.accessor('subaccountId', {
              id: 'subaccountId',
              header: 'Subaccount',
              meta: {
                minWidth: 65,
              },
              cell: (props) => {
                const { subaccount } = props.row.original
                return (
                  <YStack>
                    <BodyText>
                      {hideSubaccountLabels
                        ? subaccount.subaccount.subaccount_id
                        : subaccount.label}
                    </BodyText>
                    <BodyText color="secondary">
                      {formatMarginTypeLabel(subaccount.subaccount.margin_type)}
                    </BodyText>
                  </YStack>
                )
              },
            })
          : null,
        columnHelper.accessor('size', {
          id: 'size',
          header: 'Size',
          meta: {
            minWidth: 65,
          },
          cell: (props) => {
            const { equity, size, instrumentName } = props.row.original
            return (
              <YStack>
                <BodyText>{formatInstrumentSize(instrumentName, size)}</BodyText>
                <BodyText color="secondary">{formatUSD(equity)}</BodyText>
              </YStack>
            )
          },
        }),
        columnHelper.accessor('entryPrice', {
          id: 'entryPrice',
          header: 'Entry Price',
          meta: {
            minWidth: 80,
          },
          cell: (props) => <BodyText>{formatUSD(props.row.original.entryPrice)}</BodyText>,
        }),
        columnHelper.accessor('currentPrice', {
          id: 'currentPrice',
          header: 'Current Price',
          meta: {
            minWidth: 85,
          },
          cell: (props) => (
            <BodyText>
              {props.row.original.currentPrice ? formatUSD(props.row.original.currentPrice) : '-'}
            </BodyText>
          ),
        }),
        hasMargin
          ? columnHelper.accessor('liquidationPrice', {
              id: 'liqPrice',
              header: 'Liq / Oracle',
              meta: {
                minWidth: 80,
              },
              cell: (props) => {
                const { liquidationPrice, indexPrice, liquidationState } = props.row.original
                return liquidationPrice ? (
                  <YStack>
                    <BodyText
                      color={
                        liquidationState === 'critical'
                          ? 'red'
                          : liquidationState === 'warning'
                          ? 'amber'
                          : undefined
                      }
                    >
                      {formatUSD(liquidationPrice)}
                    </BodyText>
                    {indexPrice ? (
                      <BodyText color="secondary">{formatUSD(indexPrice)}</BodyText>
                    ) : null}
                  </YStack>
                ) : (
                  <BodyText>-</BodyText>
                )
              },
            })
          : null,
        !hidePnl
          ? columnHelper.accessor('unrealizedPnl', {
              id: 'unrealizedPL',
              header: 'Unrealized P&L',
              meta: {
                minWidth: 95,
              },
              cell: (props) => {
                const { unrealizedPnlPct, unrealizedPnl } = props.row.original
                return unrealizedPnl ? (
                  <YStack>
                    <BodyText color={unrealizedPnl && unrealizedPnl > 0 ? 'green' : 'red'}>
                      {formatUSD(unrealizedPnl, { showSign: true })}
                    </BodyText>
                    <BodyText color="secondary">
                      {unrealizedPnlPct
                        ? formatPercentage(unrealizedPnlPct, { maxDps: 2, showSign: true })
                        : '-'}
                    </BodyText>
                  </YStack>
                ) : (
                  <BodyText>-</BodyText>
                )
              },
            })
          : null,
        hasRealizedPnl && !hidePnl
          ? columnHelper.accessor('realizedPnl', {
              id: 'realizedPnl',
              header: 'Realized P&L',
              meta: {
                minWidth: 85,
              },
              cell: (props) => {
                const { realizedPnl, realizedPnlPct } = props.row.original
                return realizedPnl && realizedPnlPct ? (
                  <YStack>
                    <BodyText color={realizedPnl && realizedPnl > 0 ? 'green' : 'red'}>
                      {formatUSD(realizedPnl, { showSign: true })}
                    </BodyText>
                    <BodyText color="secondary">
                      {formatPercentage(realizedPnlPct, { maxDps: 2, showSign: true })}
                    </BodyText>
                  </YStack>
                ) : (
                  <BodyText>-</BodyText>
                )
              },
            })
          : null,
        hasMargin
          ? columnHelper.accessor('margin', {
              id: 'margin',
              header: 'Margin',
              meta: {
                minWidth: 70,
              },
              cell: (props) => {
                const margin = props.cell.getValue()
                return <BodyText>{margin ? formatUSD(margin) : '-'}</BodyText>
              },
            })
          : null,
        hasTpOrSl
          ? columnHelper.accessor('triggers', {
              id: 'triggers',
              meta: {
                minWidth: 70,
              },
              header: 'Triggers',
              cell: (props) => {
                const { openStopLoss, openTakeProfit } = props.row.original
                return openTakeProfit || openStopLoss ? (
                  <YStack>
                    <BodyText color={openTakeProfit ? 'primary' : 'secondary'}>
                      TP: {openTakeProfit ? formatUSD(openTakeProfit) : '—'}
                    </BodyText>
                    <BodyText color={openStopLoss ? 'primary' : 'secondary'}>
                      SL: {openStopLoss ? formatUSD(openStopLoss) : '—'}
                    </BodyText>
                  </YStack>
                ) : (
                  <BodyText color="secondary">-</BodyText>
                )
              },
            })
          : null,
        showActions
          ? columnHelper.accessor('close', {
              id: 'close',
              meta: {
                minWidth: 110,
                maxWidth: 110,
                isActionColumn: true,
              },
              header: () => (
                <DropdownButton
                  size="sm"
                  isPortaled
                  icon={<MoreVertical />}
                  isDisabled={!currentSubaccount}
                >
                  <DropdownButton.ListItem
                    label={hideDustPositions ? 'Show Dust' : 'Hide Dust'}
                    onPress={() =>
                      currentSubaccount
                        ? handleHideDustPositions(currentSubaccount.subaccount_id)
                        : undefined
                    }
                  />
                </DropdownButton>
              ),
              cell: (props) => {
                const { position, openStopLoss, openTakeProfit, instrumentName, isLong, size } =
                  props.row.original
                return (
                  <XStack gap="$2">
                    <PositionCloseButton
                      instrumentName={instrumentName}
                      isLong={isLong}
                      size={size}
                    />
                    <PositionActionDropdown
                      position={position}
                      openStopLoss={openStopLoss}
                      openTakeProfit={openTakeProfit}
                    />
                  </XStack>
                )
              },
            })
          : null,
      ]),
    [
      columnHelper,
      currentSubaccount,
      handleHideDustPositions,
      hasMargin,
      hasRealizedPnl,
      hasTpOrSl,
      hideDustPositions,
      hidePnl,
      hideSubaccountLabels,
      showActions,
      showSubaccountId,
    ]
  )

  return (
    <Table emptyMessage="No positions" {...tableProps} size="lg" data={rows} columns={columns} />
  )
}
